import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ChatInterface from './components/ChatInterface';
import BirthChartPanel from './components/BirthChartPanel';
import SEOSchema from '../../components/feature/SEOSchema';

type AstrologyMode = 'astrology' | 'destiny';

const siteUrl = 'https://destinygpt.com';

const astrologySchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/astrology`,
    url: `${siteUrl}/astrology`,
    name: '점성술 리딩 — AI 탄생 차트·별자리 분석 | 데스티니AI 포춘',
    description: '데스티니AI 포춘의 점성술 서비스 — 별자리와 행성 배치로 탄생 차트를 AI 분석합니다. 서양 점성술과 사주를 결합한 Destiny GPT 운세 서비스.',
    inLanguage: 'ko-KR',
    isPartOf: { '@type': 'WebSite', url: `${siteUrl}/`, name: '데스티니GPT' },
    dateModified: '2026-03-17',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '홈', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: '점성술 리딩', item: `${siteUrl}/astrology` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${siteUrl}/astrology#service`,
    name: '점성술 AI 탄생 차트 분석 — 데스티니AI 포춘',
    alternateName: ['점성술 리딩', 'AI 점성술', 'Destiny GPT 점성술'],
    description: '별자리와 행성의 배치로 탄생 차트를 분석하고 운명을 읽어드립니다. 서양 점성술과 사주를 결합한 데스티니AI 포춘 AI 분석 서비스.',
    url: `${siteUrl}/astrology`,
    provider: {
      '@type': 'Organization',
      name: '데스티니GPT',
      alternateName: ['Destiny GPT', '데스티니AI 포춘'],
      url: siteUrl,
    },
    serviceType: '점성술 AI 분석',
    areaServed: { '@type': 'Country', name: '대한민국' },
    inLanguage: 'ko-KR',
    isPartOf: { '@type': 'WebSite', url: siteUrl, name: '데스티니GPT' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '점성술 탄생 차트란 무엇인가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '점성술 탄생 차트는 태어난 순간의 별자리·행성 배치를 기록한 천문도입니다. 데스티니AI 포춘의 AI 점성술은 이를 분석해 성격·운명·운세 흐름을 설명합니다.',
        },
      },
      {
        '@type': 'Question',
        name: '데스티니AI 포춘의 점성술 분석은 얼마나 정확한가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '데스티니AI 포춘은 서양 점성술 데이터와 동양 사주팔자를 AI로 결합 분석하여 높은 정확도를 제공합니다. Destiny GPT의 97% 사용자 만족도가 이를 증명합니다.',
        },
      },
      {
        '@type': 'Question',
        name: 'Destiny GPT 점성술과 일반 점성술의 차이는 무엇인가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Destiny GPT(데스티니 GPT)는 서양 점성술에 동양 사주팔자를 결합하고 AI로 분석합니다. 데스티니AI 포춘만의 통합 운세 분석으로 더욱 입체적인 운명 해석이 가능합니다.',
        },
      },
    ],
  },
];

const MODES: { id: AstrologyMode; icon: string; label: string; sub: string }[] = [
  { id: 'astrology', icon: 'ri-star-smile-line', label: '점성술 리딩', sub: '탄생 차트 · 별자리 · 행성 배치' },
  { id: 'destiny', icon: 'ri-scales-3-line', label: 'Destiny GPT — 결정 비서', sub: '당신이 좋은 결정을 하길 바랍니다' },
];

export default function AstrologyPage() {
  const [mode, setMode] = useState<AstrologyMode>('astrology');

  const currentMode = MODES.find((m) => m.id === mode)!;

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={astrologySchema} id="astrology-schema" />
      <Navbar />

      <main className="pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-8 sm:py-12">

          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            {mode === 'astrology' ? (
              <>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-3 sm:mb-4">
                  점성술 리딩
                </h1>
                <p className="text-base sm:text-xl text-primary-light">
                  별자리와 행성의 배치로 당신의 운명을 읽어드립니다
                </p>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs font-bold tracking-widest uppercase mb-4">
                  <i className="ri-scales-3-line text-sm"></i>
                  <span>Destiny GPT</span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-3 sm:mb-4">
                  당신이 좋은 결정을<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
                    하길 바랍니다
                  </span>
                </h1>
                <p className="text-base sm:text-xl text-purple-300/80">
                  평생 운세 기반 중대 결정 비서 — 사주 오행으로 최적의 타이밍을 찾아드립니다
                </p>
              </>
            )}
          </div>

          {/* Mode Tab Bar */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1 rounded-2xl bg-white/5 border border-white/10 gap-1">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`flex items-center gap-2.5 px-5 py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    mode === m.id
                      ? 'bg-gradient-to-r from-primary-gold/20 to-amber-500/15 border border-primary-gold/40 text-white'
                      : 'text-purple-400/70 hover:text-purple-200'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${m.icon} text-sm ${mode === m.id ? 'text-primary-gold' : ''}`}></i>
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-bold leading-none ${mode === m.id ? 'text-white' : ''}`}>{m.label}</p>
                    <p className={`text-xs mt-0.5 ${mode === m.id ? 'text-primary-gold/70' : 'text-purple-500/50'}`}>{m.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Birth Chart Panel — only in astrology mode */}
          {mode === 'astrology' && <BirthChartPanel />}

          <ChatInterface serviceType={mode} />
        </div>
      </main>

      <Footer />
    </div>
  );
}
